import { useMemo, useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { KpiNumber } from "@/components/ui/kpi-number";
import { EstadoDoValor } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { CarregandoConteudo } from "@/components/ui/carregando-conteudo";
import {
  StandardizedTable,
  TableColumn,
} from "@/components/ui/standardized-table";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import {
  Search,
  TrendingUp,
  Eye,
  MousePointer,
  Settings,
  BarChart3,
  LineChart as LineChartIcon,
  RefreshCw,
  Star,
  FileText,
  Gauge,
  ArrowUp,
  ArrowDown,
  Minus,
  type LucideIcon,
} from "lucide-react";
import {
  BarChart as TremorBarChart,
  DonutChart as TremorDonutChart,
} from "@tremor/react";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import { useToast } from "@/hooks/use-toast";
import { useIntegrationStatus } from "@/hooks/useIntegrationStatus";
import {
  getAccountsApiV1IntegrationsSearchConsoleAccountsGet,
  getCompleteDataApiV1IntegrationsSearchConsoleCompleteDataGet,
  getConnectionStatusApiV1IntegrationsSearchConsoleConnectionStatusGet,
  getTopPagesApiV1IntegrationsSearchConsoleTopPagesGet,
  getTopQueriesApiV1IntegrationsSearchConsoleTopQueriesGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  SearchConsoleAccount,
  SearchConsoleCompleteResponse,
  SearchConsoleConnectionStatus,
} from "@/backend-sdk/client/types.gen";
import IntegrationErrorBoundary from "@/components/common/ErrorBoundary";
import { getKpiTooltip } from "@/lib/kpi-tooltips";

interface ChartDataPoint {
  name: string;
  impressoes: number;
  cliques: number;
  posicao: number;
}

/**
 * Uma métrica de Core Web Vitals como o CrUX a mede: o p75 da origem e a fração dos
 * carregamentos em cada faixa, numa janela de 28 dias que o próprio CrUX escolhe.
 */
interface CoreWebVitalsData {
  metric: string;
  p75: number;
  status: "good" | "needs_improvement" | "poor";
  good: number;
  needsImprovement: number;
  poor: number;
}

interface IndexingData {
  status: string;
  count: number;
  percentage: number;
  color: string;
}

interface TopQuery {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  trend: "up" | "down" | "stable";
}

interface TopPage {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  trend: "up" | "down" | "stable";
}

const metricNumber = (value: number | null | undefined): number => value ?? 0;

type LinhaDeBusca = SearchConsoleCompleteResponse["daily_metrics"][number];

/**
 * A posição média de um conjunto de linhas, ponderada por impressões — a definição do
 * Search Console. A média simples das linhas dá a um dia com uma impressão o mesmo peso de
 * um dia com dez mil (M-098).
 */
const posicaoPonderada = (linhas: readonly LinhaDeBusca[]): number => {
  let numerador = 0;
  let impressoes = 0;
  for (const linha of linhas) {
    numerador += metricNumber(linha.position) * metricNumber(linha.impressions);
    impressoes += metricNumber(linha.impressions);
  }
  return impressoes > 0 ? numerador / impressoes : 0;
};

/** Todos os números desta página saem do Search Console. */
const FONTES: readonly RequisitoDeFonte[] = ["search_console"];

interface OverviewMetrics {
  totalClicks: number;
  totalImpressions: number;
  avgCtr: number;
  avgPosition: number;
  /** Das páginas inspecionadas — a amostra das mais vistas, e não o site. */
  indexedPages: number;
  inspectedPages: number;
  /** A avaliação do CrUX (a pior das três métricas), ou `null` sem dado de campo. */
  coreWebVitalsStatus: CoreWebVitalsData["status"] | null;
}

const ROTULO_DA_FAIXA: Record<CoreWebVitalsData["status"], string> = {
  good: "Bom",
  needs_improvement: "Precisa melhorar",
  poor: "Ruim",
};

const COR_DA_FAIXA: Record<CoreWebVitalsData["status"], string> = {
  good: "text-green-600",
  needs_improvement: "text-orange-500",
  poor: "text-red-600",
};

/** LCP e INP chegam em milissegundos; o CLS é adimensional. */
const formatarP75 = (metric: string, p75: number): string =>
  metric === "CLS"
    ? p75.toLocaleString("pt-BR", { maximumFractionDigits: 2 })
    : `${(p75 / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} s`;

/** O veredito da inspeção decide a cor; o rótulo é o que o Google escreveu. */
const COR_DO_VEREDITO: Record<string, string> = {
  PASS: "#10b981",
  NEUTRAL: "#f59e0b",
  PARTIAL: "#ef4444",
  FAIL: "#ef4444",
};

const StatCard = ({
  title,
  value,
  format,
  icon: Icon,
  trend,
  subtitle,
  color = "blue",
  carregando,
  erro,
}: {
  title: string;
  /** `null` = a busca não trouxe o número. */
  value: number | null;
  carregando: boolean;
  erro: boolean;
  format: (n: number) => string;
  icon: LucideIcon;
  trend?: "up" | "down" | "stable";
  subtitle?: string;
  color?: string;
}) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0">
      <CardTitle className="flex items-center gap-1 min-w-0">
        <span className="truncate">{title}</span>
        <HelpTooltip
          content={getKpiTooltip(title)}
          triggerClassName="h-3.5 w-3.5 text-[9px]"
        />
      </CardTitle>
      <Icon className={`h-4 w-4 text-${color}-600`} />
    </CardHeader>
    <CardContent>
      <div className="min-w-0">
        <EstadoDoValor
          carregando={carregando}
          disponivel={value !== null}
          fontes={FONTES}
          erro={erro}
          className="text-2xl font-bold text-muted-foreground"
        >
          <KpiNumber
            value={Number(value ?? 0)}
            format={format}
            className="font-bold text-foreground"
            maxFontPx={30}
            breathingRoomPx={18}
            ariaLabel={title}
          />
        </EstadoDoValor>
      </div>
      {subtitle && (
        <div className="flex items-center text-xs text-muted-foreground">
          {trend === "up" && (
            <ArrowUp className="h-3 w-3 text-success-soft mr-1" />
          )}
          {trend === "down" && (
            <ArrowDown className="h-3 w-3 text-error-soft mr-1" />
          )}
          {trend === "stable" && (
            <Minus className="h-3 w-3 text-gray-500 mr-1" />
          )}
          <span>{subtitle}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const SearchConsolePage = () => {
  const { toast } = useToast();

  // Estado para o filtro global
  const { filter: filterState } = useGlobalFilter();

  // Hook de health monitoring das integrações
  const {
    health,
    status: integrationStatus,
    isLoading: healthLoading,
    forceCheck,
  } = useIntegrationStatus("search_console", "search-console-main");

  const [status, setStatus] = useState<SearchConsoleConnectionStatus | null>(
    null,
  );
  const [accounts, setAccounts] = useState<SearchConsoleAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  // Começa carregando: a primeira busca só sai no efeito, e o primeiro desenho mostrava a tela
  // de "não conectado" (e cards zerados) antes de ela começar.
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  // As falhas só viravam toast, e os cards ficavam em zero como se o site não tivesse cliques.
  const [erroDados, setErroDados] = useState(false);

  // Estados de dados
  const [completeData, setCompleteData] =
    useState<SearchConsoleCompleteResponse | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [coreWebVitalsData, setCoreWebVitalsData] = useState<
    CoreWebVitalsData[]
  >([]);
  const [indexingData, setIndexingData] = useState<IndexingData[]>([]);
  const [topQueries, setTopQueries] = useState<TopQuery[]>([]);
  const [topPages, setTopPages] = useState<TopPage[]>([]);
  // `null` até a busca trazer métricas: o zero inicial aparecia como se tivesse sido medido.
  const [overviewMetrics, setOverviewMetrics] =
    useState<OverviewMetrics | null>(null);
  // Sem dado de campo no CrUX (ou sem a chave dele no servidor), o card e a aba somem:
  // mostrar 0% afirmaria uma experiência ruim que ninguém mediu.
  const temCoreWebVitals = coreWebVitalsData.length > 0;
  // As abas são controladas porque a de Core Web Vitals pode sumir com ela aberta — uma
  // recarga que volte sem dado de campo deixaria a página numa aba sem gatilho e em branco.
  const [aba, setAba] = useState("overview");
  const abaVisivel =
    aba === "core-web-vitals" && !temCoreWebVitals ? "overview" : aba;

  const topQueriesColumns: TableColumn[] = useMemo(
    () => [
      { key: "query", header: "Consulta", type: "text", width: "36ch" },
      {
        key: "clicks",
        header: "Cliques",
        type: "number",
        render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
      },
      {
        key: "impressions",
        header: "Impressões",
        type: "number",
        render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
      },
      {
        key: "ctr",
        header: "CTR",
        type: "percent",
        render: (value) =>
          `${Number(value ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
      },
      {
        key: "position",
        header: "Posição",
        type: "number",
        render: (value) =>
          Number(value ?? 0).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      },
    ],
    [],
  );

  const topPagesColumns: TableColumn[] = useMemo(
    () => [
      { key: "page", header: "Página", type: "text", width: "36ch" },
      {
        key: "clicks",
        header: "Cliques",
        type: "number",
        render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
      },
      {
        key: "impressions",
        header: "Impressões",
        type: "number",
        render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
      },
      {
        key: "ctr",
        header: "CTR",
        type: "percent",
        render: (value) =>
          `${Number(value ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
      },
      {
        key: "position",
        header: "Posição",
        type: "number",
        render: (value) =>
          Number(value ?? 0).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      },
    ],
    [],
  );

  const paginasInspecionadasColumns: TableColumn[] = useMemo(
    () => [
      { key: "page_url", header: "Página", type: "text", width: "44ch" },
      {
        key: "impressions",
        header: "Impressões",
        type: "number",
        width: "12ch",
        render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
      },
      {
        key: "coverage_state",
        header: "Estado no Google",
        type: "text",
        width: "30ch",
        render: (value, row) => (
          <Badge
            variant={
              row.verdict === "PASS"
                ? "secondary"
                : row.verdict === "NEUTRAL"
                  ? "outline"
                  : "destructive"
            }
          >
            {String(value ?? "Sem informação")}
          </Badge>
        ),
      },
      {
        key: "last_crawl_time",
        header: "Último rastreio",
        type: "date",
        width: "16ch",
        render: (value) =>
          value ? new Date(String(value)).toLocaleDateString("pt-BR") : "N/D",
      },
    ],
    [],
  );

  // Função para formatar datas
  const formatDateForAPI = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  // Calcular período padrão (período com dados no seed: 2025-06-23 até 2025-07-15)
  const getDefaultDateRange = () => {
    return {
      startDate: "2025-06-23",
      endDate: "2025-07-15",
    };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadingData(true);
      setErroDados(false);

      // Verificar status da conexão
      const { data: connectionStatus } =
        await getConnectionStatusApiV1IntegrationsSearchConsoleConnectionStatusGet(
          { throwOnError: true },
        );
      setStatus(connectionStatus);

      // Carregar contas
      const { data: accountsData } =
        await getAccountsApiV1IntegrationsSearchConsoleAccountsGet({
          throwOnError: true,
        });
      setAccounts(accountsData);

      if (accountsData.length > 0) {
        const firstAccount = accountsData[0];

        if (!selectedAccount) {
          setSelectedAccount(firstAccount.id);

          toast({
            title: "Carregando Dados",
            description: "Buscando dados do Search Console...",
          });

          await loadAccountData(firstAccount.id);

          toast({
            title: "Dados Carregados",
            description: "Dados do Search Console carregados com sucesso!",
          });
        } else {
          await loadAccountData(selectedAccount);
        }
      } else {
        // Usar conta criada no seed quando não há OAuth
        const seedAccountId = "de46a22f-0b4d-477a-a216-47359828d871";

        try {
          toast({
            title: "Carregando Dados",
            description: "Buscando dados do Search Console do banco...",
          });

          // Carregar dados usando account ID do seed
          await loadAccountData(seedAccountId);

          toast({
            title: "Dados Carregados",
            description: "Dados do Search Console carregados com sucesso!",
          });
        } catch (seedError) {
          // Limpar dados se não conseguir carregar do seed
          setCompleteData(null);
          setChartData([]);
          setCoreWebVitalsData([]);
          setIndexingData([]);
          setTopQueries([]);
          setTopPages([]);
          setOverviewMetrics(null);
        }
      }
    } catch (error) {
      setErroDados(true);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do Search Console",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setLoadingData(false);
    }
  };

  const loadAccountData = async (accountId: string) => {
    try {
      setLoadingData(true);

      const { startDate, endDate } = getDefaultDateRange();

      const janela = {
        account_id: accountId,
        start_date: startDate,
        end_date: endDate,
      };
      // `daily_metrics` é a série do site, um dia por linha: dela saem o gráfico e os
      // quatro cards. Consultas e páginas vêm das rotas próprias, que agregam no recorte
      // certo — somar a série por consulta e página não fecha com o Search Console (M-098).
      // As tabelas fazem as próprias buscas no Google; uma que falhe esvazia só a aba
      // dela, e não derruba a série que já chegou.
      const [completo, consultas, paginas] = await Promise.all([
        getCompleteDataApiV1IntegrationsSearchConsoleCompleteDataGet({
          query: janela,
          throwOnError: true,
        }),
        getTopQueriesApiV1IntegrationsSearchConsoleTopQueriesGet({
          query: { ...janela, limit: 10 },
          throwOnError: true,
        }).catch(() => null),
        getTopPagesApiV1IntegrationsSearchConsoleTopPagesGet({
          query: { ...janela, limit: 10 },
          throwOnError: true,
        }).catch(() => null),
      ]);
      const { data } = completo;
      setCompleteData(data);

      if (data && data.daily_metrics.length > 0) {
        // Um dia por linha: o gráfico é a série como veio, em ordem de data.
        const processedChartData = [...data.daily_metrics]
          .sort((a, b) => a.date.localeCompare(b.date))
          .map((dia) => ({
            name: new Date(`${dia.date}T00:00:00`).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
            }),
            impressoes: metricNumber(dia.impressions),
            cliques: metricNumber(dia.clicks),
            posicao: Number(metricNumber(dia.position).toFixed(2)),
          }));

        setChartData(processedChartData);

        // Core Web Vitals: uma linha por métrica, do CrUX. Lista vazia quer dizer que não
        // há dado de campo (ou a chave do CrUX não está configurada) — o card some.
        setCoreWebVitalsData(
          data.core_web_vitals.map((cwv) => ({
            metric: cwv.metric,
            p75: cwv.p75,
            status: cwv.status,
            good: cwv.good_share * 100,
            needsImprovement: cwv.needs_improvement_share * 100,
            poor: cwv.poor_share * 100,
          })),
        );

        // Indexação: a amostra das páginas mais vistas, agrupada pelo rótulo do Google.
        const indexGrouped = data.index_coverage.reduce(
          (acc, index) => {
            const rotulo = index.coverage_state ?? "Sem informação";
            acc[rotulo] ??= { count: 0, verdict: index.verdict };
            acc[rotulo].count++;
            return acc;
          },
          {} as Record<string, { count: number; verdict: string }>,
        );
        const totalInspecionadas = data.index_coverage.length;
        setIndexingData(
          Object.entries(indexGrouped)
            .map(([status, grupo]) => ({
              status,
              count: grupo.count,
              percentage: (grupo.count / totalInspecionadas) * 100,
              color: COR_DO_VEREDITO[grupo.verdict] ?? "#6b7280",
            }))
            .sort((a, b) => b.count - a.count),
        );

        // A ordem é a das rotas: mais impressões primeiro. `avg_ctr` já é percentual.
        setTopQueries(
          (consultas?.data ?? []).map((consulta) => ({
            query: consulta.query ?? "Desconhecido",
            clicks: consulta.total_clicks,
            impressions: consulta.total_impressions,
            ctr: consulta.avg_ctr,
            position: consulta.avg_position,
            trend: "stable" as const, // TODO: Implementar cálculo de tendência
          })),
        );
        setTopPages(
          (paginas?.data ?? []).map((pagina) => ({
            page: pagina.page ?? "Desconhecido",
            clicks: pagina.total_clicks,
            impressions: pagina.total_impressions,
            ctr: pagina.avg_ctr,
            position: pagina.avg_position,
            trend: "stable" as const, // TODO: Implementar cálculo de tendência
          })),
        );

        // Calcular métricas de overview
        const totalClicks = data.daily_metrics.reduce(
          (sum, metric) => sum + metricNumber(metric.clicks),
          0,
        );
        const totalImpressions = data.daily_metrics.reduce(
          (sum, metric) => sum + metricNumber(metric.impressions),
          0,
        );
        const avgCtr =
          totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
        const avgPosition = posicaoPonderada(data.daily_metrics);

        const indexedPages = data.index_coverage.filter(
          (index) => index.verdict === "PASS",
        ).length;

        // A avaliação de Core Web Vitals é a pior das três métricas — a regra do Google.
        const faixas = data.core_web_vitals.map((cwv) => cwv.status);
        const coreWebVitalsStatus =
          faixas.length < 3
            ? null
            : faixas.includes("poor")
              ? "poor"
              : faixas.includes("needs_improvement")
                ? "needs_improvement"
                : "good";

        setOverviewMetrics({
          totalClicks,
          totalImpressions,
          avgCtr,
          avgPosition,
          indexedPages,
          inspectedPages: data.index_coverage.length,
          coreWebVitalsStatus,
        });
      } else {
        // Sem série, nada da conta anterior pode ficar na tela.
        setChartData([]);
        setTopQueries([]);
        setTopPages([]);
        setCoreWebVitalsData([]);
        setIndexingData([]);
        setOverviewMetrics(null);
      }
    } catch (error) {
      setErroDados(true);
      // Nada da conta anterior pode ficar desenhado sob o seletor da nova.
      setCompleteData(null);
      setChartData([]);
      setTopQueries([]);
      setTopPages([]);
      setCoreWebVitalsData([]);
      setIndexingData([]);
      setOverviewMetrics(null);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados da conta",
        variant: "destructive",
      });
    } finally {
      setLoadingData(false);
    }
  };

  // Carregar dados na inicialização
  useEffect(() => {
    const timer = setTimeout(() => {
      loadData();
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Se não há dados e não está carregando, mostrar estado vazio
  if (
    !loading &&
    !loadingData &&
    (!status || !status.is_connected) &&
    accounts.length === 0 &&
    !completeData
  ) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Google Search Console
            </h1>
            <p className="text-muted-foreground">
              Monitore a performance do seu site nos resultados de busca do
              Google
            </p>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="bg-gray-50 text-gray-700">
              Não Conectado
            </Badge>
            <Button variant="outline">
              <Settings className="h-4 w-4 mr-2" />
              Conectar
            </Button>
          </div>
        </div>

        <div className="text-center py-12">
          <Search className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Search Console não conectado
          </h3>
          <p className="text-muted-foreground mb-4">
            Conecte sua conta do Google Search Console para ver dados reais de
            SEO.
          </p>
          <Button onClick={() => {}}>
            <Settings className="h-4 w-4 mr-2" />
            Conectar Search Console
          </Button>
        </div>
      </div>
    );
  }

  return (
    <IntegrationErrorBoundary
      integrationType="search_console"
      integrationId="search-console-main"
    >
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Google Search Console
            </h1>
            <p className="text-muted-foreground">
              Monitore a performance do seu site nos resultados de busca do
              Google
            </p>
          </div>
          <div className="flex gap-2">
            <Badge
              variant="outline"
              className={
                loadingData || loading
                  ? "bg-blue-50 text-blue-700"
                  : status?.is_connected
                    ? "bg-green-50 text-green-700"
                    : "bg-gray-50 text-gray-700"
              }
            >
              {loadingData || loading
                ? "🔄 Carregando dados..."
                : status?.is_connected
                  ? "Conectado"
                  : "Carregando..."}
            </Badge>
            {status?.last_sync && (
              <div className="text-xs text-muted-foreground">
                Última sync:{" "}
                {new Date(status.last_sync).toLocaleString("pt-BR")}
              </div>
            )}
          </div>
        </div>

        {/* Filtro Global */}
        <GlobalFilter className="mb-6" />

        {/* Métricas Principais */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <>
            <StatCard
              title="Total de Cliques"
              value={overviewMetrics?.totalClicks ?? null}
              carregando={loading}
              erro={erroDados}
              format={(n) =>
                new Intl.NumberFormat("pt-BR").format(Math.trunc(n))
              }
              icon={MousePointer}
              subtitle="últimos 30 dias"
              color="blue"
            />
            <StatCard
              title="Impressões"
              value={overviewMetrics?.totalImpressions ?? null}
              carregando={loading}
              erro={erroDados}
              format={(n) =>
                new Intl.NumberFormat("pt-BR").format(Math.trunc(n))
              }
              icon={Eye}
              subtitle="últimos 30 dias"
              color="green"
            />
            <StatCard
              title="CTR Médio"
              value={overviewMetrics?.avgCtr ?? null}
              carregando={loading}
              erro={erroDados}
              format={(n) => `${Number(n).toFixed(2).replace(".", ",")}%`}
              icon={TrendingUp}
              subtitle="taxa de cliques"
              color="purple"
            />
            <StatCard
              title="Posição Média"
              value={overviewMetrics?.avgPosition ?? null}
              carregando={loading}
              erro={erroDados}
              format={(n) => Number(n).toFixed(2).replace(".", ",")}
              icon={Star}
              subtitle="nos resultados"
              color="orange"
            />
          </>
        </div>

        <Tabs value={abaVisivel} onValueChange={setAba} className="space-y-4">
          <TabsList
            className={`grid w-full grid-cols-2 sm:grid-cols-3 gap-1 ${
              temCoreWebVitals ? "md:grid-cols-5" : "md:grid-cols-4"
            }`}
          >
            <TabsTrigger value="overview" className="text-xs sm:text-sm">
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="queries" className="text-xs sm:text-sm">
              Consultas
            </TabsTrigger>
            <TabsTrigger value="pages" className="text-xs sm:text-sm">
              Páginas
            </TabsTrigger>
            {temCoreWebVitals && (
              <TabsTrigger
                value="core-web-vitals"
                className="text-xs sm:text-sm"
              >
                Core Web Vitals
              </TabsTrigger>
            )}
            <TabsTrigger value="indexing" className="text-xs sm:text-sm">
              Indexação
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {/* Gráfico Principal */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LineChartIcon className="h-5 w-5" />
                  Performance nos Resultados de Busca
                </CardTitle>
                <CardDescription>
                  Evolução de impressões, cliques e posição média ao longo do
                  tempo
                </CardDescription>
              </CardHeader>
              <CardContent className="pl-2">
                {loading ? (
                  <div className="h-[300px] flex items-center justify-center">
                    <div className="text-center">
                      <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Carregando dados...
                      </p>
                    </div>
                  </div>
                ) : chartData.length > 0 ? (
                  <ScrollableChart>
                    <TremorBarChart
                      className="h-72"
                      data={chartData}
                      index="name"
                      categories={["impressoes", "cliques", "posicao"]}
                      colors={["blue", "emerald", "amber"]}
                      valueFormatter={(value) => value.toLocaleString("pt-BR")}
                      showLegend={true}
                      showGridLines={true}
                      showAnimation={true}
                    />
                  </ScrollableChart>
                ) : (
                  <div className="h-[300px] flex items-center justify-center">
                    <div className="text-center">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold mb-2">
                        Nenhum dado disponível
                      </h3>
                      <p className="text-muted-foreground">
                        Não há dados de performance para exibir no período
                        selecionado.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Cards */}
            <div
              className={`grid gap-4 ${temCoreWebVitals ? "md:grid-cols-2" : ""}`}
            >
              {/* Core Web Vitals — só com dado de campo do CrUX */}
              {temCoreWebVitals && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gauge className="h-5 w-5" />
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <span className="truncate">Core Web Vitals</span>
                        <HelpTooltip
                          content="A avaliação de campo do Google (Chrome UX Report) para a origem do site, nos últimos 28 dias: aprovado só quando LCP, INP e CLS estão na faixa boa."
                          triggerClassName="h-3.5 w-3.5 text-[9px]"
                        />
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div
                        className={`text-3xl font-bold mb-2 ${
                          overviewMetrics?.coreWebVitalsStatus
                            ? COR_DA_FAIXA[overviewMetrics.coreWebVitalsStatus]
                            : "text-muted-foreground"
                        }`}
                      >
                        <EstadoDoValor
                          carregando={loading}
                          disponivel={
                            overviewMetrics?.coreWebVitalsStatus != null
                          }
                          fontes={FONTES}
                          erro={erroDados}
                          className="text-muted-foreground"
                        >
                          {overviewMetrics?.coreWebVitalsStatus
                            ? ROTULO_DA_FAIXA[
                                overviewMetrics.coreWebVitalsStatus
                              ]
                            : ""}
                        </EstadoDoValor>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        experiência de quem visita{" "}
                        {/* O CrUX mede uma origem; numa propriedade de domínio, pode não
                            ser a da loja — por isso ela aparece escrita. */}
                        <span className="font-medium">
                          {completeData?.core_web_vitals[0]?.origin.replace(
                            /^https?:\/\//,
                            "",
                          )}
                        </span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Páginas Indexadas — sobre a amostra inspecionada */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <span className="inline-flex items-center gap-1 min-w-0">
                      <span className="truncate">Páginas Indexadas</span>
                      <HelpTooltip
                        content="O Google não publica o relatório de cobertura por API. Estas são as páginas com mais impressões nos últimos 28 dias, inspecionadas uma a uma, uma vez por dia — é uma amostra, e não o site inteiro."
                        triggerClassName="h-3.5 w-3.5 text-[9px]"
                      />
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">
                      <EstadoDoValor
                        carregando={loading}
                        disponivel={
                          overviewMetrics !== null &&
                          overviewMetrics.inspectedPages > 0
                        }
                        fontes={FONTES}
                        erro={erroDados}
                        className="text-muted-foreground"
                      >
                        {`${overviewMetrics?.indexedPages ?? 0} de ${
                          overviewMetrics?.inspectedPages ?? 0
                        }`}
                      </EstadoDoValor>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      das páginas mais vistas
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="queries" className="space-y-4">
            <StandardizedTable
              title="Top Consultas de Busca"
              description="Consultas que mais aparecem para o seu site na busca do Google"
              columns={topQueriesColumns}
              data={topQueries}
              loading={loading || loadingData}
            />
          </TabsContent>

          <TabsContent value="pages" className="space-y-4">
            <StandardizedTable
              title="Top Páginas"
              description="Páginas do seu site que mais aparecem na busca do Google"
              columns={topPagesColumns}
              data={topPages}
              loading={loading || loadingData}
            />
          </TabsContent>

          <TabsContent value="core-web-vitals" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              {coreWebVitalsData.map((metric) => (
                <Card key={metric.metric}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{metric.metric}</span>
                      <span
                        className={`text-sm font-medium ${COR_DA_FAIXA[metric.status]}`}
                      >
                        {ROTULO_DA_FAIXA[metric.status]}
                      </span>
                    </CardTitle>
                    <CardDescription>
                      p75: {formatarP75(metric.metric, metric.p75)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(
                        [
                          ["Bom", metric.good, "bg-green-500"],
                          [
                            "Precisa Melhorar",
                            metric.needsImprovement,
                            "bg-orange-500",
                          ],
                          ["Ruim", metric.poor, "bg-red-500"],
                        ] as const
                      ).map(([rotulo, fatia, cor]) => (
                        <div
                          key={rotulo}
                          className="flex justify-between items-center"
                        >
                          <span className="text-sm">{rotulo}</span>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 ${cor} rounded`}></div>
                            <span className="text-sm font-medium">
                              {fatia.toLocaleString("pt-BR", {
                                maximumFractionDigits: 1,
                              })}
                              %
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Fração dos carregamentos de página em cada faixa, medida pelo
              Chrome UX Report nos últimos 28 dias.
            </p>
          </TabsContent>

          <TabsContent value="indexing" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Status de Indexação</CardTitle>
                  <CardDescription>
                    As páginas com mais impressões, inspecionadas no Google —
                    uma amostra, e não o site inteiro
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {indexingData.length === 0 && (loading || loadingData) && (
                      <CarregandoConteudo />
                    )}
                    {indexingData.map((item) => (
                      <div key={item.status} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">
                            {item.status}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {item.count} ({item.percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <Progress value={item.percentage} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Distribuição por Status</CardTitle>
                </CardHeader>
                <CardContent>
                  {indexingData.length > 0 ? (
                    <TremorDonutChart
                      className="h-52"
                      data={indexingData}
                      index="status"
                      category="count"
                      colors={["emerald", "rose", "amber"]}
                      valueFormatter={(value) => value.toLocaleString("pt-BR")}
                      showLabel={true}
                      showAnimation={true}
                    />
                  ) : loading || loadingData ? (
                    <CarregandoConteudo className="h-[200px]" />
                  ) : (
                    <div className="h-[200px] flex items-center justify-center">
                      <p className="text-muted-foreground">
                        Nenhum dado de indexação disponível
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            {(indexingData.length > 0 || loading || loadingData) && (
              <StandardizedTable
                title="Páginas inspecionadas"
                description="O estado de cada página no índice do Google, na inspeção de hoje"
                columns={paginasInspecionadasColumns}
                data={completeData?.index_coverage ?? []}
                loading={loading || loadingData}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </IntegrationErrorBoundary>
  );
};

export default SearchConsolePage;
